/**
 * Option lists for the writer profile. Keys are stable ids stored on the server;
 * labels are i18n keys with English defaults.
 */
export interface Option {
  value: string;
  key: string;
  label: string;
  emoji?: string;
  hint?: string;
  hintKey?: string;
}

export const FORMAT_OPTIONS: Option[] = [
  { value: 'novel', key: 'writerProfile.formats.novel', label: 'Novel', emoji: '📖' },
  {
    value: 'short_story',
    key: 'writerProfile.formats.shortStory',
    label: 'Short story',
    emoji: '📝',
  },
  {
    value: 'screenplay',
    key: 'writerProfile.formats.screenplay',
    label: 'Screenplay',
    emoji: '🎬',
  },
  { value: 'stage_play', key: 'writerProfile.formats.stagePlay', label: 'Stage play', emoji: '🎭' },
  {
    value: 'fan_fiction',
    key: 'writerProfile.formats.fanFiction',
    label: 'Fan fiction',
    emoji: '✨',
  },
  { value: 'poetry', key: 'writerProfile.formats.poetry', label: 'Poetry', emoji: '🪶' },
];

/** Genre values are stored as-is on stories and profiles; labels are translated. */
export const GENRE_OPTIONS: Option[] = [
  { value: 'Romance', key: 'writerProfile.genres.romance', label: 'Romance' },
  { value: 'Fantasy', key: 'writerProfile.genres.fantasy', label: 'Fantasy' },
  { value: 'Sci-Fi', key: 'writerProfile.genres.sciFi', label: 'Sci-Fi' },
  { value: 'Thriller', key: 'writerProfile.genres.thriller', label: 'Thriller' },
  { value: 'Horror', key: 'writerProfile.genres.horror', label: 'Horror' },
  { value: 'Literary Fiction', key: 'writerProfile.genres.literary', label: 'Literary Fiction' },
  { value: 'Mystery', key: 'writerProfile.genres.mystery', label: 'Mystery' },
  { value: 'Adventure', key: 'writerProfile.genres.adventure', label: 'Adventure' },
  { value: 'Drama', key: 'writerProfile.genres.drama', label: 'Drama' },
  { value: 'Comedy', key: 'writerProfile.genres.comedy', label: 'Comedy' },
  { value: 'Historical', key: 'writerProfile.genres.historical', label: 'Historical' },
  { value: 'Dystopian', key: 'writerProfile.genres.dystopian', label: 'Dystopian' },
];

export interface StyleAxis {
  key: string;
  labelKey: string;
  label: string;
  left: { value: string; key: string; label: string };
  right: { value: string; key: string; label: string };
}

export const STYLE_AXES: StyleAxis[] = [
  {
    key: 'prose',
    labelKey: 'writerProfile.style.prose',
    label: 'Prose',
    left: { value: 'spare', key: 'writerProfile.style.spare', label: 'Spare' },
    right: { value: 'lush', key: 'writerProfile.style.lush', label: 'Lush' },
  },
  {
    key: 'register',
    labelKey: 'writerProfile.style.register',
    label: 'Register',
    left: { value: 'literary', key: 'writerProfile.style.literary', label: 'Literary' },
    right: { value: 'commercial', key: 'writerProfile.style.commercial', label: 'Commercial' },
  },
  {
    key: 'mood',
    labelKey: 'writerProfile.style.mood',
    label: 'Mood',
    left: { value: 'dark', key: 'writerProfile.style.dark', label: 'Dark' },
    right: { value: 'light', key: 'writerProfile.style.light', label: 'Light' },
  },
  {
    key: 'balance',
    labelKey: 'writerProfile.style.balance',
    label: 'Balance',
    left: {
      value: 'dialogue-driven',
      key: 'writerProfile.style.dialogue',
      label: 'Dialogue-driven',
    },
    right: {
      value: 'description-rich',
      key: 'writerProfile.style.description',
      label: 'Description-rich',
    },
  },
];

export const GOAL_OPTIONS: Option[] = [
  {
    value: 'finish_draft',
    key: 'writerProfile.goals.finishDraft',
    label: 'Finish a draft',
    emoji: '🏁',
    hintKey: 'writerProfile.goals.finishDraftHint',
    hint: 'Get a complete first draft on the page',
  },
  {
    value: 'get_unstuck',
    key: 'writerProfile.goals.getUnstuck',
    label: 'Get unstuck',
    emoji: '🔑',
    hintKey: 'writerProfile.goals.getUnstuckHint',
    hint: 'Break through blocks and keep momentum',
  },
  {
    value: 'improve_craft',
    key: 'writerProfile.goals.improveCraft',
    label: 'Sharpen my craft',
    emoji: '✂️',
    hintKey: 'writerProfile.goals.improveCraftHint',
    hint: 'Tighter scenes, better dialogue, stronger structure',
  },
  {
    value: 'explore',
    key: 'writerProfile.goals.explore',
    label: 'Just explore',
    emoji: '🧭',
    hintKey: 'writerProfile.goals.exploreHint',
    hint: 'Play with ideas and see what happens',
  },
];

export const EXPERIENCE_OPTIONS: Option[] = [
  { value: 'beginner', key: 'writerProfile.experience.beginner', label: 'Just starting' },
  { value: 'hobbyist', key: 'writerProfile.experience.hobbyist', label: 'Write for fun' },
  { value: 'serious', key: 'writerProfile.experience.serious', label: 'Serious about it' },
  { value: 'professional', key: 'writerProfile.experience.professional', label: 'Professional' },
];
